// tslint:disable:max-line-length
import {IFileDesc} from './file.types';
import { number } from 'prop-types';


export enum EcoAgreementStatus {
  AWAITING_RESPONSE = 'AWAITING_RESPONSE',
  APPROVED = 'APPROVED',
  PRE_APPROVED = 'PRE_APPROVED', // spawned by an approved agreement or creator has 100% of split
  REJECTED = 'REJECTED',
  DELETED = 'DELETED',
}

export enum EcoPurchaseStatus {
  APPROVED = 'APPROVED',
  DONE_WITHOUT_APPROVAL = 'DONE_WITHOUT_APPROVAL', // matter of fact purchase is made
}

export enum EcoRepeat {
  ONCE = 'ONCE',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  TERM = 'TERM',
  YEARLY = 'YEARLY',
}

export enum EcoTransferMethod {
  SWISH = 'SWISH',
  BANK = 'BANK',
  CASH = 'CASH',
  VENMO = 'VENMO',
  PAYPAL = 'PAYPAL',
  CASHAPP = 'CASHAPP',
  OTHER = 'OTHER',
}

export interface IEcoAgreement {
  id:number;
  chatId?:number; // chat regarding this agreement
  status:EcoAgreementStatus; // will always match responses below:
  responses:IEcoAgreementResponse[]; // only parents can respond. 1 response and approved:true = awaiting response, 1 response and approved:false = closed, 2 approved:true = appproved, 2 responses and one is approved:false = rejected
  forDate:number; // date when created or period this is for.
  closed:boolean; // no more changes or purchases can be made to this contract

  title:string; // if repeat !== ONCE then display title based on title + forDate eg title
  //  = "Supscription" and forDate = 1681849701000 display: Supscription (April)
  children: number[]; // who is the purchase for. empty array = general purchase for noone in particular
  amount?:number; // the estimated amount. If undefined means that cost is whatever the cost is
  currency:string; // 3 letter ISO currency standard.
  description?:string;
  images:IFileDesc[];   // up to 3 images
  responsible:number[]; // id of the accounts who are intended to do the purchase (can be empty or several accounts)
  repeat:EcoRepeat;
  split:IEcoSplit[]; // the sum of the percentages is always = 100

  spawnedChild:boolean; // pending...

  archivedBy:number[]; // list of account ids that hides this.
  seenBy:number[]; // account ids that has seen this contract

  pendingChange?:IEcoPendingChange;
}

export interface IEcoPendingChange {
  id:number;
  status:EcoAgreementStatus; // will only be AWAIITNG, DELETED, REJECTED,
  responses:IEcoAgreementResponse[];

  amount?:number; // the new estimated amount
  currency:string; // 3 letter ISO currency standard
  split:IEcoSplit[]; // the sum of the percentages is always = 100

  archivedBy:number[]; // list of account ids that hides this.
}

export interface IEcoPurchase {
  id:number;
  creator:number;
  agreementId?:number; // agreement that this purchase refers to
  chatId?:number; // if is has a agreement Id it should copy this value.
  date:number; // unix time. when created

  title:string;
  amount:number; // amount paid
  currency:string;
  description?:string;
  images:IFileDesc[];  // up to 3 images

  paidBy:number; // account id who paid
  split:IEcoSplit[]; // the sum of the percentages is always = 100.
  children: number[]; // who is the purchase for. empty array = general purchase for noone in particular

  isApproved:boolean; // if withing amount of agreement
  fromSharedAccount:boolean;

  seenBy:number[]; // account ids that has seen this purchase
  archivedBy:number[]; // account ids that has archived this purchase
  disputedBy?:number; // can only be removed by this user ID, or delete it or split 100% to other parent.
  deleted:boolean; // this was an error
  deletedBy?:number; // account that deleted it. missing if deleted before this was recorded
}

export interface IEcoTransfer {
  id:number;
  creator:number;
  from:number; // 0 === shared
  to:number; // 0 === shared
  date:number; // unix time. when created
  booking?:number; // unix time. for date it should be booked
  sum:number;
  currency:string;
  method:EcoTransferMethod;
  note?:string;
  disputedBy?:number;
  chatId?:number;
  deleted:boolean;
  deletedBy?:number; // account that deleted it. missing if deleted before this was recorded
  archivedBy:number[]; // account ids that has archived this transfer
  seenBy:number[]; // account ids that has seen this transfer  
}

export interface IEcoAgreementResponse {
  date:number; // unix time
  account:number; // id of the account that responded
  approved:boolean; // if approved or rejected
}

export interface IEcoSplit {
  accountId:number;
  percentage:number;
}

export interface IEcoSplitSetting {
  start:number;
  split:IEcoSplit[];
}

// ==============================
// == REQUESTS TO SERVER BELOW ==
// ==============================

export interface INewEcoAgreement { //
  chatId?:number; // chat regarding this agreement
  title:string; // if repeat !== ONCE then display title based on title + forDate eg title = "Supscription" and forDate = 1681849701000 display: Supscription (April)
  children: number[]; // who is the purchase for. empty array = general purchase for noone in particular
  amount?:number; // the estimated amount. unset amount = no price set
  currency:string; // 3 letter ISO currency standard
  description?:string;
  images:number[];  // up to 3 images (ids of files)
  responsible:number[]; // id of the accounts who are intended to do the purchase (can be empty or several accounts)
  repeat:EcoRepeat;
  split:IEcoSplit[]; // the sum of the percentages is always = 100
  autoAccept?:boolean;
}

export interface IUpdateEcoAgreement {
  id:number;

  chatId?:number;
  title:string;
  children: number[];
  description?:string;
  images:number[];  // up to 3 images (ids of files)
  responsible:number[];
  repeat?:EcoRepeat;

  // will trigger a pending change
  amount?:number;
  currency:string;
  split:IEcoSplit[]; // the sum of the percentages is always = 100
}

export interface IRespondEcoAgreement {
  agreementId:number; // id of the agreement
  approved:boolean; // if approved or rejected
}

export interface IRespondEcoAgreementUpdate {
  agreementId:number; // id of the agreement
  changeId:number; // id of the pending change we respond to
  approved:boolean; // if approved or rejected
}

export interface IEndEcoAgreement {
  agreementId:number; // id of the agreement to end
  endChildSpawning:boolean; // if it should stop spawning children
  purchase:boolean;
}


export interface INewEcoPurchase {
  agreementId?:number; // agreement that this purchase refers to

  title:string;
  amount:number; // amount paid
  currency:string;
  description?:string;
  images:number[];  // up to 3 images
  children:number[]; // account ids that has seen this purchase
  split:IEcoSplit[]; // the sum of the percentages is always = 100.

  fromSharedAccount:boolean;
  paidBy:number;
  date?:number;

  closeAgreement?:boolean; // default is true
  startAsDisputed:boolean; // will cause the purchase to be disputed by other parent
}

export interface IUpdateEcoPurchase {
  purchaseId:number;
  chatId?:number;
  title:string;
  amount:number; // amount paid
  currency:string;
  description?:string;
  paidByMe:boolean;
  images:number[];  // up to 3 images
  children:number[]; // account ids that has seen this purchase
  split:IEcoSplit[]; // the sum of the percentages is always = 100.
  fromSharedAccount:boolean;
  date?:number;
}

export interface INewEcoTransfer {
  from:number; // 0 === shared
  to:number; // 0 === shared
  sum:number;
  currency:string;
  method:EcoTransferMethod;
  note?:string;
  booking?:number;
}

export interface IUpdateEcoTransfer {
  id:number;
  from:number; // 0 === shared
  to:number; // 0 === shared
  sum:number;
  method:EcoTransferMethod;
  note?:string;
  disputedBy?:number;
  chatId?:number;
  booking?:number;
}

export interface IEcoSetting {
  startTime:number;
	currency:string;
	accountIdOne:number;
	accountIdTwo:number;
	transferIdOne:string;
	transferIdTwo:string;
	percentageIdOne:number;
	percentageIdTwo:number;
  venmoOne: string;
  venmoTwo: string;
  cashappOne: string;
  cashappTwo: string;
  paypalOne: string;
  paypalTwo: string;
  paymentMethod: "SWISH" | "VENMO" | "CASHAPP" | "PAYPAL";
}



// WEBPACK FOOTER //
// ./src/app-types/websocket/economy.types.ts